import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Divider,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close, Add, Delete } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { getStudentByIdentifier } from '@/features/users/usersThunks';
import { setTemporalUserData } from '@/features/auth/temporalUserSlice';
import { useNavigate, useLocation } from 'react-router-dom';

interface Student {
  id: string;
  name: string;
  identifier: string;
}

interface AttendantRegistryModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  email: string;
  cc: string;
  students: Student[];
}

interface FormErrors {
  email?: string;
  cc?: string;
  students?: string;
}

const AttendantRegistryModal: React.FC<AttendantRegistryModalProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const temporalUserData = useAppSelector((state) => state.temporalUser);

  const [formData, setFormData] = useState<FormData>({
    email: '',
    cc: '',
    students: [],
  });

  const [studentIdentifier, setStudentIdentifier] = useState('');
  const [searchingStudent, setSearchingStudent] = useState(false);
  const [studentSearchError, setStudentSearchError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateEmail = (email: string): boolean => {
    return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  };

  const validateCC = (cc: string): boolean => {
    return /^\d{6,10}$/.test(cc);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, email: value }));

    if (errors.email) {
      if (validateEmail(value)) {
        setErrors(prev => ({ ...prev, email: undefined }));
      }
    }
  };

  const handleCCChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Solo números
    setFormData(prev => ({ ...prev, cc: value }));

    if (errors.cc) {
      if (validateCC(value)) {
        setErrors(prev => ({ ...prev, cc: undefined }));
      }
    }
  };

  const handleAddStudent = async () => {
    if (!studentIdentifier.trim()) {
      setStudentSearchError('Ingrese el identificador del estudiante');
      return;
    }

    // Verificar si el estudiante ya está en la lista
    if (formData.students.some(s => s.identifier === studentIdentifier.trim())) {
      setStudentSearchError('Este estudiante ya ha sido agregado');
      return;
    }

    setSearchingStudent(true);
    setStudentSearchError(null);

    try {
      const result = await dispatch(getStudentByIdentifier(studentIdentifier.trim())).unwrap();

      const student: Student = {
        id: result.id || Date.now().toString(),
        name: result.displayName || 'Estudiante sin nombre',
        identifier: result.identifier || studentIdentifier.trim(),
      };

      setFormData(prev => ({
        ...prev,
        students: [...prev.students, student],
      }));

      setStudentIdentifier('');
      setErrors(prev => ({ ...prev, students: undefined }));
    } catch (error: any) {
      setStudentSearchError(error || 'Estudiante no encontrado. Verifique el identificador.');
    } finally {
      setSearchingStudent(false);
    }
  };

  const handleRemoveStudent = (id: string) => {
    setFormData(prev => ({
      ...prev,
      students: prev.students.filter(s => s.id !== id),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    // Validaciones
    const newErrors: FormErrors = {};

    if (!formData.email) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Ingrese un correo electrónico válido';
    }

    if (!formData.cc) {
      newErrors.cc = 'La cédula de ciudadanía es requerida';
    } else if (!validateCC(formData.cc)) {
      newErrors.cc = 'La cédula debe tener entre 6 y 10 dígitos';
    }

    if (formData.students.length === 0) {
      newErrors.students = 'Debe agregar al menos un estudiante';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Guardar datos en Redux y navegar
    setIsLoading(true);
    try {
      // Guardar datos temporales en Redux
      dispatch(setTemporalUserData(formData));

      setSubmitSuccess(true);

      // Verificar si ya estamos en payments/portal
      const isInPaymentsPortal = location.pathname === '/payments/portal';

      setTimeout(() => {
        if (isInPaymentsPortal) {
          // Si ya estamos en el portal, solo cerrar el modal
          setFormData({ email: '', cc: '', students: [] });
          setSubmitSuccess(false);
          onClose();
        } else {
          // Si no estamos en el portal, navegar
          navigate('/payments/portal');
          setFormData({ email: '', cc: '', students: [] });
          setSubmitSuccess(false);
          onClose();
        }
      }, 1000);
    } catch (error) {
      setSubmitError('Error al procesar los datos. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      // Si estamos en payments/portal, solo permitir cerrar si los datos están completos
      const isInPaymentsPortal = location.pathname === '/payments/portal';

      if (isInPaymentsPortal && !temporalUserData.isComplete) {
        // No permitir cerrar si estamos en payments portal y los datos no están completos
        return;
      }

      setFormData({ email: '', cc: '', students: [] });
      setStudentIdentifier('');
      setStudentSearchError(null);
      setErrors({});
      setSubmitError(null);
      setSubmitSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          Datos de quien realiza el pago
        </Typography>
        <IconButton onClick={handleClose} size="small" disabled={isLoading}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ pt: 2 }}>
          {/* Email */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5, color: 'text.primary' }}>
            Correo Electrónico
          </Typography>
          <TextField
            fullWidth
            name="email"
            placeholder="ejemplo@correo.com"
            value={formData.email}
            onChange={handleEmailChange}
            error={Boolean(errors.email)}
            helperText={errors.email}
            disabled={isLoading}
            sx={{ mb: 2 }}
            InputProps={{
              sx: {
                backgroundColor: '#fafafa',
                borderRadius: '8px',
                '& fieldset': { border: 'none' },
              }
            }}
          />

          {/* CC */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5, color: 'text.primary' }}>
            Cédula de Ciudadanía
          </Typography>
          <TextField
            fullWidth
            name="cc"
            placeholder="Ingrese número de cédula"
            value={formData.cc}
            onChange={handleCCChange}
            error={Boolean(errors.cc)}
            helperText={errors.cc}
            disabled={isLoading}
            sx={{ mb: 3 }}
            InputProps={{
              sx: {
                backgroundColor: '#fafafa',
                borderRadius: '8px',
                '& fieldset': { border: 'none' },
              }
            }}
          />

          {/* Students Section */}
          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Estudiantes a Cargo
          </Typography>

          {/* Add Student Form */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Identificador del estudiante (cédula o TI)"
              value={studentIdentifier}
              onChange={(e) => {
                setStudentIdentifier(e.target.value);
                setStudentSearchError(null);
              }}
              disabled={isLoading || searchingStudent}
              sx={{ flex: 1 }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddStudent();
                }
              }}
              InputProps={{
                sx: {
                  backgroundColor: '#fafafa',
                  borderRadius: '8px',
                  '& fieldset': { border: 'none' },
                }
              }}
            />
            <IconButton
              color="primary"
              onClick={handleAddStudent}
              disabled={isLoading || searchingStudent}
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': { backgroundColor: 'primary.dark' },
                '&:disabled': { backgroundColor: '#ccc' },
              }}
            >
              {searchingStudent ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Add />}
            </IconButton>
          </Box>

          {studentSearchError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {studentSearchError}
            </Alert>
          )}

          {errors.students && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.students}
            </Alert>
          )}

          {/* Students List */}
          {formData.students.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Estudiantes agregados:
              </Typography>
              {formData.students.map((student) => (
                <Chip
                  key={student.id}
                  label={`${student.name} (ID: ${student.identifier})`}
                  onDelete={() => handleRemoveStudent(student.id)}
                  deleteIcon={<Delete />}
                  disabled={isLoading}
                  sx={{ m: 0.5 }}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              ¡Registro exitoso! Redirigiendo...
            </Alert>
          )}

          {/* Submit Button */}
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={isLoading}
            sx={{
              mt: 2,
              py: 1.2,
              backgroundColor: 'secondary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              'Continuar al portal de pagos'
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AttendantRegistryModal;
